import cn from 'classnames'
import {
  IHRDatasetInfo,
  IHRSettings,
} from 'legacy/lut/classes/helpers/partner360/HistoricalResultsHelper'
import { IFormActualsTargetsSettings } from 'legacy/lut/classes/models/partner360/IFormActualsTargetsSettings'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { selectHasMultiLocalCurrencies } from 'legacy/lut/features/partner360/configuration/selectors/selectHasMultiLocalCurrencies'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { forwardRef, Ref, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations/components/translation'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { Button } from 'core-components/library/button'
import { LoaderOverlay } from 'core-components/library/loader'
import MeasureController from 'core/analytics/MeasureController'
import { useMeasureController } from 'core/analytics/useMeasureController'
import { HttpCancel } from 'core/classes/http'
import Logger from 'core/utils/Logger'
import { QBRSectionAvailableWhenFinalized } from 'partner360/components/QBR/shared/qbrSectionThatCanBeDisabled/QBRSectionAvailableWhenFinalized'
import { SelectedPeriodWithPercent } from 'partner360/components/QBR/shared/selectedPeriodWithPercent/SelectedPeriodWithPercent'
import { selectQbrSelectedPeriod } from 'partner360/features/QBR/selectors/selectQbrSelectedPeriod'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import {
  changeActualsType,
  changeCurrency,
  setAvailableActualsTypes,
  setAvailableCurrencies,
} from '../../../../../features/partner360/configuration'
import { IJBPPlanning } from '../models/IJBPPlanning'
import { fetchQbrPlanningData } from '../services/api/fetchQbrPlanningData'
import { fetchQbrPlanningSettings } from '../services/api/fetchQbrPlanningSettings'
import { WidgetHeader } from '../widgetHeader/WidgetHeader'

import { IS_ALWAYS_SHOW_ACTUALS } from './config'
import { CategoryPlanningStandard } from './lookAndFeel/CategoryPlanningStandard'
import { TSplitType } from './models'
import { IPlanningRef } from './models/IPlanningRef'
import tr from './translations'

import styles from './QbrPlanning.module.scss'

interface IProps {
  widgetParams: IObjectAny
  metrics: MeasureController
  documentId: number
  fYear: number
  currency: string
  netNdp: string
  rounding: string
}

export const QbrPlanning = forwardRef((props: IProps, ref: Ref<IPlanningRef>) => {
  useImperativeHandle(ref, () => ({ updatePlanning }))

  /** -------------------- State ----------------------- */

  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [hrDataSet, setHRDataSet] = useState<IHRDatasetInfo>()
  const [hrDataSets, setHRDataSets] = useState<IHRDatasetInfo[]>([])
  const [hrFormats, setHRFormats] = useState<IFormActualsTargetsSettings[]>([])
  const [isInitialized, setIsInitialized] = useState(false)
  const [isNoData, setIsNoData] = useState(false)
  const [isRegionalPlanning, setIsRegionalPlanning] = useState(true)
  const [isSettingsLoaded, setIsSettingsLoaded] = useState(false)
  const [isShowActuals, setIsShowActuals] = useState(false)
  const [isShowQuota, setIsShowQuota] = useState(false)
  const [planningData, setPlanningData] = useState<IJBPPlanning[]>([])
  const [originalActualsValueType, setOriginalActualsValueType] = useState('')
  const [splitType, setSplitType] = useState<TSplitType>('Total')
  const [tabId, setTabId] = useState('financial')
  const [isShowComposedSoB, setIsShowComposedSoB] = useState(false)
  const [isShowPrevTargets, setIsShowPrevTargets] = useState(false)

  /** -------------------- Hooks ----------------------- */

  const dispatch = useAppDispatch()
  const { startLoadData, stopLoadData } = useMeasureController(props.metrics)
  const t = useSafetyTranslation()

  /** -------------------- Variables ----------------------- */

  const selectedPeriod = useSelector(selectQbrSelectedPeriod)
  const hasMultiLocalCurrencies = useSelector(selectHasMultiLocalCurrencies)
  const currency = hasMultiLocalCurrencies ? 'USD' : props.currency
  const isShowLoader = !isSettingsLoaded || !isDataLoaded
  const isShowCancelRequest = isInitialized && isShowLoader
  const isHideWidget = !isInitialized

  /** -------------------- Methods ----------------------- */

  const fetchPlanningSettings = async (cancelObject?: HttpCancel) => {
    setIsSettingsLoaded(false)

    const settingsResponse = await fetchQbrPlanningSettings<IFormActualsTargetsSettings>(
      props.documentId,
      cancelObject
    )
    const settingsData: IFormActualsTargetsSettings[] = (settingsResponse?.Data || []).map(
      (el) => ({
        ...el,
        ProgramTypes: ProgramTypes.PersonalSystems,
      })
    )
    setIsNoData(!settingsData.length)

    if (settingsData.length) {
      setHRFormats(settingsData)

      let category = ''

      const categoriesSettings: IHRDatasetInfo[] = []
      settingsData.forEach((el) => {
        let settings: IHRSettings = {}
        try {
          settings = JSON.parse(el.Settings)
        } catch {
          Logger.error('fail to parse hrsettings at qbr planning')
        }

        if (
          category !== el.Category &&
          !categoriesSettings.find((hi) => hi.category === el.Category)
        ) {
          category = el.Category

          // Temporally hide MPS Summary Chart
          if (!el.SummaryProgram) {
            categoriesSettings.push({
              category: category,
              categoryTranslation: t(el.Category),
              subCategory: el.SubCategory,
              colors: (el.Colors || '').split(';'),
              ProgramType: el.ProgramType,
              SummaryProgram: !!el.SummaryProgram,
              Settings: settings,
              IsContractualsAvailable: el.IsContractualsAvailable,
              IsSoBAvailable: el.IsSoBAvailable,
              IsSoBTotalCalc: el.IsSoBTotalCalc,
              IsUnitsAvailable: el.IsUnitsAvailable,
              IsHideFinancial: el.IsHideFinancial,
              IsOnlyQuarterlyPlanning: el.IsOnlyQuarterlyPlanning,
              IsMainHierarchy: el.IsMainHierarchy,
              MandatoryType: el.MandatoryType,
              MandatoryByFlags: el.MandatoryByFlags,
              UnitsMandatoryByFlags: el.UnitsMandatoryByFlags,
            })
          }
        }
      })
      setHRDataSets(categoriesSettings)
      if (categoriesSettings && categoriesSettings.length) {
        setHRDataSet(categoriesSettings[0])
      }
    }

    setIsSettingsLoaded(true)
  }

  const setOriginalSwitcherValues = (allPlanningData: IJBPPlanning[]) => {
    // Set Currency and ActualsValueType by first Targets data row
    const targetData = allPlanningData.filter((el) => !!el.IsTarget)
    if (targetData.length) {
      if (targetData[0].Currency) {
        //setCurrency(targetData[0].Currency)
        //setOriginalCurrency(targetData[0].Currency)
      }
      if (targetData[0].NetNdp) {
        //setActualsValueType(targetData[0].NetNdp)
        setOriginalActualsValueType(targetData[0].NetNdp)
      }
    }
  }

  /** -------------------- User actions handlers ----------------------- */

  const updatePlanning = async () => {
    setIsDataLoaded(false)
    setIsInitialized(true)
    startLoadData()

    setHRDataSets([])
    setHRDataSet(undefined)
    setHRFormats([])

    fetchPlanningSettings(cancelObject)

    const planningResult = await fetchQbrPlanningData<IJBPPlanning>(props.documentId, cancelObject)
    const allPlanningData = planningResult?.Data || []
    setIsNoData(!allPlanningData.length)
    setPlanningData(allPlanningData)
    setOriginalSwitcherValues(allPlanningData)

    stopLoadData()
    setIsDataLoaded(true)
  }

  const onApplyChanges = (data: IJBPPlanning[]) => {
    const allPlanningData = data || []
    setPlanningData(allPlanningData)
    setOriginalSwitcherValues(allPlanningData)
  }

  const cancelDataRequest = () => {
    if (cancelObject) {
      cancelObject.cancel()
    }
  }

  /** -------------------- Effects ----------------------- */

  const cancelObject = useMemo(() => new HttpCancel(), [])
  const periodFQuarters = useMemo(() => {
    return selectedPeriod.fQuarters.map(({ fQuarter }) => fQuarter)
  }, [selectedPeriod.fQuarters])

  const tabs = useMemo(
    () => [
      {
        id: 'financial',
        content: !hrDataSet ? (
          <></>
        ) : (
          <>
            <SelectedPeriodWithPercent />
            <CategoryPlanningStandard
              key={`jbp-planning-${hrDataSet?.category}-total`}
              {...props}
              pid=""
              planningData={planningData}
              mapData={[]}
              dataSetInfo={hrDataSet}
              allDataSets={hrDataSets}
              formats={hrFormats.filter((el) => el.Category === hrDataSet?.category)}
              showActuals={isShowActuals || IS_ALWAYS_SHOW_ACTUALS}
              setShowActuals={(show) => setIsShowActuals(show)}
              showQuota={isShowQuota}
              setShowQuota={(show) => setIsShowQuota(show)}
              isShowFutureYearsPlans={false}
              isRegionalPlanning={isRegionalPlanning}
              setIsRegionalPlanning={(isRegionalPlanning) =>
                setIsRegionalPlanning(isRegionalPlanning)
              }
              isContractualPlanning={false}
              isShowOnlyCompletedPlans={false}
              setIsShowOnlyCompletedPlans={() => {}}
              onApply={(data) => onApplyChanges(data)}
              originalCurrency={currency}
              originalActualsValueType={originalActualsValueType}
              partnerFlagsState={[]}
              isQuarterlyBusinessReviewMode
              quarterlyBusinessReviewFYear={props.fYear}
              quarterlyBusinessReviewNetNdp={props.netNdp}
              quarterlyBusinessReviewCurrency={currency}
              splitType={splitType}
              setSplitType={setSplitType}
              periodFQuarters={periodFQuarters}
              isShowComposedSoB={isShowComposedSoB}
              setIsShowComposedSoB={setIsShowComposedSoB}
              isShowPrevTargets={isShowPrevTargets}
              setIsShowPrevTargets={setIsShowPrevTargets}
            />
          </>
        ),
        title: 'qbr-planning-financial',
      },
      {
        id: 'units',
        content: !hrDataSet ? (
          <></>
        ) : (
          <CategoryPlanningStandard
            key={`jbp-planning-${hrDataSet?.category}-total`}
            {...props}
            pid=""
            planningData={planningData}
            mapData={[]}
            dataSetInfo={hrDataSet}
            allDataSets={hrDataSets}
            formats={hrFormats.filter((el) => el.Category === hrDataSet?.category)}
            showActuals={isShowActuals || IS_ALWAYS_SHOW_ACTUALS}
            setShowActuals={(show) => setIsShowActuals(show)}
            showQuota={isShowQuota}
            setShowQuota={(show) => setIsShowQuota(show)}
            isShowFutureYearsPlans={false}
            isRegionalPlanning={isRegionalPlanning}
            setIsRegionalPlanning={(isRegionalPlanning) =>
              setIsRegionalPlanning(isRegionalPlanning)
            }
            isContractualPlanning={false}
            isShowOnlyCompletedPlans={false}
            setIsShowOnlyCompletedPlans={() => {}}
            onApply={(data) => onApplyChanges(data)}
            originalCurrency={currency}
            originalActualsValueType={originalActualsValueType}
            partnerFlagsState={[]}
            isQuarterlyBusinessReviewMode
            isQuarterlyBusinessReviewUnits
            quarterlyBusinessReviewFYear={props.fYear}
            quarterlyBusinessReviewNetNdp={props.netNdp}
            quarterlyBusinessReviewCurrency={currency}
            splitType={splitType}
            setSplitType={setSplitType}
            periodFQuarters={periodFQuarters}
            isShowComposedSoB={isShowComposedSoB}
            setIsShowComposedSoB={setIsShowComposedSoB}
            isShowPrevTargets={isShowPrevTargets}
            setIsShowPrevTargets={setIsShowPrevTargets}
          />
        ),
        title: 'qbr-planning-units',
      },
    ],
    [hrDataSet, planningData, periodFQuarters, currency]
  )

  useEffect(() => {
    // init netNdp
    dispatch(setAvailableActualsTypes([props.netNdp as ActualsValueTypes]))
    dispatch(changeActualsType(props.netNdp as ActualsValueTypes))
    // init currency
    dispatch(setAvailableCurrencies([currency]))
    dispatch(changeCurrency(currency))
  }, [dispatch, props.netNdp, currency])

  useEffect(() => {
    if (props.documentId) {
      updatePlanning()
    }
  }, [props.documentId, currency, props.netNdp])

  /** -------------------- Render ----------------------- */

  if (isHideWidget) {
    return <></>
  }

  if (isNoData) {
    return <WidgetHeader>{tr.noDataWarning}</WidgetHeader>
  }

  const tab = tabs.find(({ id }) => id === tabId)

  return (
    <div className={styles.root}>
      {isShowCancelRequest && (
        <Button onClick={cancelDataRequest}>
          <Translation text="jbp-cancel-request" span />
        </Button>
      )}

      <div className={styles.root}>
        <LoaderOverlay show={isShowLoader} />
        <div>
          <QBRSectionAvailableWhenFinalized className={styles.header}>
            {tabs.map(({ id, title }) => (
              <Button
                variant="text"
                className={cn(styles.tabButton, styles.button, { [styles.selected]: id === tabId })}
                key={id}
                onClick={() => setTabId(id)}
              >
                {typeof title === 'string' ? <Translation text={title} span /> : title}
              </Button>
            ))}
          </QBRSectionAvailableWhenFinalized>
          {!tab ? (
            <></>
          ) : (
            <div key={tab.id} className={cn(styles.tabContent, styles.content)}>
              {tab.content}
            </div>
          )}
        </div>
      </div>
    </div>
  )
})
